import { useState } from 'react';
import { useAuthStore } from '@munawb/hooks';
import { useIncidents, useCreateIncident } from '@munawb/hooks';
import { useNotifications, useMarkNotificationsRead } from '@munawb/hooks';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { createIncidentSchema, type CreateIncidentInput } from '@munawb/validators';
import { supabase } from '@munawb/supabase';
import type { IncidentRow } from '@munawb/types';
import { RISK_LEVEL_COLORS, formatSlaTime } from '../lib/utils';

type Department  = { id: string; name: string };
type Category    = { id: string; main_cat: string; sub_cat?: string };
type TabId       = 'my-incidents' | 'report';

export default function EmployeePage() {
  const { profile } = useAuthStore();
  const [activeTab, setActiveTab]     = useState<TabId>('my-incidents');
  const [departments]                 = useState<Department[]>([]);
  const [categories]                  = useState<Category[]>([]);
  const [reportSuccess, setReportSuccess] = useState(false);

  // My incidents
  const { data: incidents = [], isLoading } = useIncidents({
    reporter_id: profile?.id,
    include_closed: true,
  });

  // Notifications
  const { data: notifications = [] } = useNotifications(
    profile?.id ?? '',
    null,
    true
  );
  const { mutate: markRead } = useMarkNotificationsRead(profile?.id ?? '');

  // Create incident form
  const { mutateAsync: createIncident } = useCreateIncident();
  const {
    register,
    handleSubmit,
    reset,
    watch,
    formState: { errors, isSubmitting },
  } = useForm<CreateIncidentInput>({
    resolver: zodResolver(createIncidentSchema),
    defaultValues: { likelihood: 1, impact: 1, is_repeated: false },
  });

  const likelihood = watch('likelihood', 1);
  const impact     = watch('impact', 1);
  const riskScore  = likelihood * impact;
  const riskLevel  = computeRiskLevel(riskScore);

  const onSubmit = async (data: CreateIncidentInput) => {
    try {
      await createIncident({ ...data, reporter_name: profile?.name ?? '' });
      setReportSuccess(true);
      reset();
      setTimeout(() => setReportSuccess(false), 4000);
      setActiveTab('my-incidents');
    } catch (err: unknown) {
      console.error('[EmployeePage] create incident error:', err);
    }
  };

  const handleSignOut = async () => {
    await supabase.auth.signOut();
  };

  const unreadCount = notifications.filter(n => !n.is_read).length;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 dark:from-slate-950 dark:to-blue-950">

      {/* Header */}
      <header className="glass-panel border-b border-slate-200/80 dark:border-slate-700/50 sticky top-0 z-30">
        <div className="max-w-5xl mx-auto px-4 h-14 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src="/munawb-logo.png" alt="" className="w-8 h-8 rounded-xl" />
            <span className="font-black text-slate-800 dark:text-white text-base">مُناوِب</span>
          </div>
          <div className="flex items-center gap-3">
            {/* Notifications bell */}
            <button
              className="relative p-2 rounded-xl hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
              onClick={() => unreadCount > 0 && markRead(notifications.filter(n => !n.is_read).map(n => n.id))}
            >
              <svg className="w-5 h-5 text-slate-600 dark:text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                  d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
              </svg>
              {unreadCount > 0 && (
                <span className="absolute top-1 right-1 w-4 h-4 bg-red-500 rounded-full text-white text-[10px] font-black flex items-center justify-center">
                  {unreadCount > 9 ? '9+' : unreadCount}
                </span>
              )}
            </button>
            <span className="text-xs font-bold text-slate-600 dark:text-slate-400">{profile?.name}</span>
            <button onClick={handleSignOut} className="btn-secondary text-xs px-3 py-1.5">
              خروج
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 py-6">

        {/* Success banner */}
        {reportSuccess && (
          <div className="mb-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-2xl p-4 flex items-center gap-3">
            <svg className="w-5 h-5 text-green-600 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
            </svg>
            <p className="text-green-700 dark:text-green-400 text-sm font-bold">تم إرسال الحدث بنجاح</p>
          </div>
        )}

        {/* Tabs */}
        <div className="flex gap-2 mb-6">
          {([
            { id: 'my-incidents', label: 'أحداثي' },
            { id: 'report',       label: 'الإبلاغ عن حدث' },
          ] as { id: TabId; label: string }[]).map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`px-5 py-2.5 rounded-xl font-black text-sm transition-all ${
                activeTab === tab.id
                  ? 'bg-blue-600 text-white shadow-sm'
                  : 'bg-white dark:bg-slate-800 text-slate-600 dark:text-slate-400 border border-slate-200 dark:border-slate-700 hover:bg-slate-50'
              }`}
            >
              {tab.label}
              {tab.id === 'my-incidents' && incidents.length > 0 && (
                <span className={`mr-2 px-1.5 py-0.5 rounded-full text-[10px] ${
                  activeTab === 'my-incidents' ? 'bg-white/20' : 'bg-slate-100 dark:bg-slate-700 text-slate-500'
                }`}>
                  {incidents.length}
                </span>
              )}
            </button>
          ))}
        </div>

        {/* My Incidents Tab */}
        {activeTab === 'my-incidents' && (
          <div className="space-y-3">
            {isLoading ? (
              <div className="glass-panel rounded-2xl p-8 text-center">
                <span className="spinner border-blue-200 border-t-blue-600" />
              </div>
            ) : incidents.length === 0 ? (
              <div className="glass-panel rounded-2xl p-10 text-center">
                <p className="text-slate-400 font-bold text-sm">لا توجد أحداث مسجلة بعد</p>
                <button
                  onClick={() => setActiveTab('report')}
                  className="btn-primary mt-4 text-sm"
                >
                  أبلغ عن حدث جديد
                </button>
              </div>
            ) : (
              incidents.map(inc => <IncidentCard key={inc.id} incident={inc} />)
            )}
          </div>
        )}

        {/* Report Tab */}
        {activeTab === 'report' && (
          <div className="glass-panel rounded-2xl p-6">
            <h2 className="text-base font-black text-slate-800 dark:text-white mb-6">الإبلاغ عن حدث جديد</h2>
            <form onSubmit={handleSubmit(onSubmit)} className="space-y-5" noValidate>

              {/* Location */}
              <div>
                <label className="block text-xs font-black text-slate-600 dark:text-slate-400 mb-1.5">
                  الموقع <span className="text-red-500">*</span>
                </label>
                <input type="text" placeholder="قسم الطوارئ — الدور الأول" className="input-field" {...register('location')} />
                {errors.location && <p className="text-red-500 text-xs font-bold mt-1">{errors.location.message}</p>}
              </div>

              {/* Event Type */}
              <div>
                <label className="block text-xs font-black text-slate-600 dark:text-slate-400 mb-1.5">
                  نوع الحدث <span className="text-red-500">*</span>
                </label>
                <input type="text" placeholder="سقوط مريض / خطأ دوائي / ..." className="input-field" {...register('event_type')} />
                {errors.event_type && <p className="text-red-500 text-xs font-bold mt-1">{errors.event_type.message}</p>}
              </div>

              {/* Likelihood + Impact */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-black text-slate-600 dark:text-slate-400 mb-1.5">
                    الاحتمالية (1–5) <span className="text-red-500">*</span>
                  </label>
                  <select className="input-field" {...register('likelihood', { valueAsNumber: true })}>
                    {[1, 2, 3, 4, 5].map(n => <option key={n} value={n}>{n}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-black text-slate-600 dark:text-slate-400 mb-1.5">
                    التأثير (1–5) <span className="text-red-500">*</span>
                  </label>
                  <select className="input-field" {...register('impact', { valueAsNumber: true })}>
                    {[1, 2, 3, 4, 5].map(n => <option key={n} value={n}>{n}</option>)}
                  </select>
                </div>
              </div>

              {/* Risk Score Preview */}
              <div className={`flex items-center gap-3 p-3 rounded-xl border ${RISK_LEVEL_COLORS[riskLevel]?.bg ?? 'bg-slate-50 border-slate-200'}`}>
                <span className="text-xs font-black text-slate-600 dark:text-slate-400">درجة الخطورة:</span>
                <span className={`font-black text-sm ${RISK_LEVEL_COLORS[riskLevel]?.text ?? 'text-slate-700'}`}>
                  {riskScore} — {riskLevel}
                </span>
              </div>

              {/* Description */}
              <div>
                <label className="block text-xs font-black text-slate-600 dark:text-slate-400 mb-1.5">
                  وصف الحدث <span className="text-red-500">*</span>
                </label>
                <textarea
                  rows={4}
                  placeholder="اكتب وصفاً تفصيلياً للحدث..."
                  className="input-field resize-none"
                  {...register('description')}
                />
                {errors.description && <p className="text-red-500 text-xs font-bold mt-1">{errors.description.message}</p>}
              </div>

              {/* Is Repeated */}
              <label className="flex items-center gap-3 cursor-pointer">
                <input type="checkbox" className="w-4 h-4 rounded" {...register('is_repeated')} />
                <span className="text-sm font-bold text-slate-700 dark:text-slate-300">حدث متكرر</span>
              </label>

              {/* Reference */}
              <div>
                <label className="block text-xs font-black text-slate-600 dark:text-slate-400 mb-1.5">
                  رقم مرجعي (اختياري)
                </label>
                <input type="text" placeholder="REF-2024-001" className="input-field" dir="ltr" {...register('reference')} />
              </div>

              <button type="submit" className="btn-primary w-full py-3" disabled={isSubmitting}>
                {isSubmitting ? (
                  <><span className="spinner border-white/30 border-t-white" /> جارٍ الإرسال...</>
                ) : (
                  'إرسال الحدث'
                )}
              </button>
            </form>
          </div>
        )}

      </main>
    </div>
  );
}

/* ── Sub-components ─────────────────────────────────────────────── */

function IncidentCard({ incident }: { incident: IncidentRow }) {
  const colors = RISK_LEVEL_COLORS[incident.risk_level] ?? RISK_LEVEL_COLORS['منخفض'];
  const sla    = formatSlaTime(incident.created_at, incident.sla_minutes, incident.status);

  return (
    <div className="glass-panel rounded-2xl p-5">
      <div className="flex items-start justify-between gap-3 mb-3">
        <div>
          <span className="text-xs font-black text-slate-400 dark:text-slate-500">{incident.event_id}</span>
          <h3 className="text-sm font-black text-slate-800 dark:text-white mt-0.5">{incident.event_type}</h3>
          <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">{incident.location}</p>
        </div>
        <div className="flex flex-col items-end gap-1.5 shrink-0">
          <span className={`px-2.5 py-1 rounded-full text-xs font-black border ${colors.badge}`}>
            {incident.risk_level}
          </span>
          <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${
            incident.status === 'مغلق' || incident.status === 'RCA Completed'
              ? 'bg-slate-100 dark:bg-slate-700 text-slate-500'
              : incident.status === 'مصعد'
              ? 'bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-400'
              : 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400'
          }`}>
            {incident.status}
          </span>
        </div>
      </div>
      <p className="text-xs text-slate-600 dark:text-slate-400 line-clamp-2 mb-3">{incident.description}</p>
      <div className="flex items-center justify-between text-[11px] text-slate-400 dark:text-slate-500">
        <span>{new Date(incident.created_at).toLocaleDateString('ar-SA')}</span>
        {sla && (
          <span className={sla.breached ? 'text-red-500 font-bold' : 'text-slate-400'}>
            {sla.breached ? 'تجاوز SLA' : `SLA: ${sla.remaining}`}
          </span>
        )}
      </div>
    </div>
  );
}

/* ── Helpers ────────────────────────────────────────────────────── */
function computeRiskLevel(score: number): string {
  if (score >= 20) return 'حرج';
  if (score >= 15) return 'مرتفع';
  if (score >= 10) return 'عالي';
  if (score >= 5)  return 'متوسط';
  return 'منخفض';
}
